import React, { useState } from "react";
import {
  FileSpreadsheet,
  ArrowLeft,
  Download,
  FileText,
  Info,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../common_components/Toast";

const Reports = () => {
  const navigate = useNavigate();
  const { handleDownload } = useAuth();
  const toast = useToast();
  const [downloading, setDownloading] = useState<string | null>(null);

  const triggerDownload = async (endpoint: string, reportName: string) => {
    setDownloading(reportName);
    try {
      const res = await handleDownload(`/quarters/reports/${endpoint}`, `${reportName}.xlsx`);
      if (res.success) {
        toast.success(`${reportName} report downloaded successfully!`);
      } else {
        throw new Error(res.error || "Download failed");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to download report");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <button
            onClick={() => navigate("/quarters")}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-bold mb-2 group transition-colors"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight font-serif flex items-center gap-3">
            <FileSpreadsheet className="w-8 h-8 text-blue-600" /> Housing Reports Center
          </h1>
        </div>
      </div>

      {/* Grid of Reports */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
        {/* Report 1: Occupancy Report */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm flex flex-col justify-between hover:shadow-md transition">
          <div className="space-y-3">
            <div className="bg-blue-50 text-blue-600 p-3 rounded-2xl w-fit">
              <FileSpreadsheet className="w-6 h-6" />
            </div>
            <h2 className="text-lg font-bold text-slate-800 font-serif">Occupancy & Inventory Status</h2>
            <p className="text-slate-500 text-xs leading-relaxed">
              Downloads a complete inventory list of all buildings, floors, and flats. Includes flat statuses (vacant, occupied, reserved) and current occupant names/emails.
            </p>
          </div>
          <button
            onClick={() => triggerDownload("occupancy", "quarters_occupancy_report")}
            disabled={downloading !== null}
            className="mt-6 w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white font-bold py-3 rounded-xl transition shadow-lg shadow-blue-200 flex items-center justify-center gap-1.5 text-xs uppercase font-sans"
          >
            <Download className="w-4.5 h-4.5" />
            {downloading === "quarters_occupancy_report" ? "Compiling spreadsheet..." : "Download Occupancy Excel"}
          </button>
        </div>

        {/* Report 2: Applications Report */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm flex flex-col justify-between hover:shadow-md transition">
          <div className="space-y-3">
            <div className="bg-indigo-50 text-indigo-600 p-3 rounded-2xl w-fit">
              <FileText className="w-6 h-6" />
            </div>
            <h2 className="text-lg font-bold text-slate-800 font-serif">Applications & Roster Scores</h2>
            <p className="text-slate-500 text-xs leading-relaxed">
              Downloads the list of all housing applications (excluding drafts) sorted by score. Includes employee ID, department, designation, computed score, and waitlist position.
            </p>
          </div>
          <button
            onClick={() => triggerDownload("applications", "quarters_applications_report")}
            disabled={downloading !== null}
            className="mt-6 w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-400 text-white font-bold py-3 rounded-xl transition shadow-lg shadow-indigo-200 flex items-center justify-center gap-1.5 text-xs uppercase font-sans"
          >
            <Download className="w-4.5 h-4.5" />
            {downloading === "quarters_applications_report" ? "Compiling spreadsheet..." : "Download Applications Excel"}
          </button>
        </div>
      </div>

      {/* Audit Guide Info */}
      <div className="bg-slate-100 border border-slate-200 text-slate-700 p-6 rounded-3xl max-w-4xl mt-10 space-y-3">
        <h3 className="font-bold text-sm flex items-center gap-1.5 font-serif text-slate-800">
          <Info className="w-4.5 h-4.5 text-blue-600" /> Export Information
        </h3>
        <p className="text-xs leading-relaxed">
          The reports generated here represent live database transactions. To inspect configurations audits, allocations logs, or manual override histories, please consult the <strong>Audit Logs</strong> tab located on the dashboard.
        </p>
      </div>
    </div>
  );
};

export default Reports;
